'use client';

import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import type { SvgIcon } from '../../icon/icon';
import {
  DrawerMenuCheckboxItem,
  DrawerMenuDescriptionItem,
  DrawerMenuDivider,
  DrawerMenuGroup,
  DrawerMenuHeading,
  DrawerMenuItem,
  DrawerMenuRadioGroup,
  DrawerMenuRadioItem,
} from '../drawer-menu/drawer-menu-item';
import {
  PopoverMenuCheckboxItem,
  PopoverMenuDescriptionItem,
  PopoverMenuDivider,
  PopoverMenuGroup,
  PopoverMenuHeading,
  PopoverMenuItem,
  PopoverMenuRadioGroup,
  PopoverMenuRadioItem,
} from '../popover-menu/popover-menu-item';
import type { MenuDescriptionItemTextProps } from '../shared/menu-description-item';
import type { MenuItemVariantsProps } from '../shared/menu-item-variants';
import { useDrawerOrPopoverMenu } from './drawer-or-popover-menu-context';

/**
 * What a row accepts in both menus. The popover rows are Base UI items on a div, the
 * drawer rows are buttons, so the attributes are typed on their common element.
 * children is restated because react-i18next widens it in the consuming apps.
 */
type RowProps = Omit<HTMLAttributes<HTMLElement>, 'children' | 'className' | 'color' | 'title'> & {
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
};

// children restated for the same react-i18next reason as on rows
type BlockProps = Omit<HTMLAttributes<HTMLElement>, 'children' | 'className' | 'color'> & {
  children?: ReactNode;
  className?: string;
};

export type DrawerOrPopoverMenuItemProps = RowProps & {
  /** Element to render the row as, e.g. an `<a href>`; the row content goes inside it. */
  render?: ReactElement;
  icon?: SvgIcon;
  variant?: MenuItemVariantsProps['variant'];
  /** @default true */
  closeOnClick?: boolean;
};

export const DrawerOrPopoverMenuItem = (props: DrawerOrPopoverMenuItemProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? <PopoverMenuItem {...props} /> : <DrawerMenuItem {...props} />;
};

export type DrawerOrPopoverMenuDescriptionItemProps = Omit<
  DrawerOrPopoverMenuItemProps,
  'icon' | 'children'
> &
  Pick<MenuDescriptionItemTextProps, 'title' | 'description' | 'classNames' | 'children'>;

export const DrawerOrPopoverMenuDescriptionItem = (
  props: DrawerOrPopoverMenuDescriptionItemProps,
) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? (
    <PopoverMenuDescriptionItem {...props} />
  ) : (
    <DrawerMenuDescriptionItem {...props} />
  );
};

export type DrawerOrPopoverMenuCheckboxItemProps = RowProps & {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** @default false */
  closeOnClick?: boolean;
};

export const DrawerOrPopoverMenuCheckboxItem = (props: DrawerOrPopoverMenuCheckboxItemProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? (
    <PopoverMenuCheckboxItem {...props} />
  ) : (
    <DrawerMenuCheckboxItem {...props} />
  );
};

export type DrawerOrPopoverMenuRadioGroupProps = Omit<BlockProps, 'defaultValue'> & {
  value?: string;
  onValueChange?: (value: string) => void;
};

export const DrawerOrPopoverMenuRadioGroup = (props: DrawerOrPopoverMenuRadioGroupProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? (
    <PopoverMenuRadioGroup {...props} />
  ) : (
    <DrawerMenuRadioGroup {...props} />
  );
};

export type DrawerOrPopoverMenuRadioItemProps = RowProps & {
  value: string;
  /** @default false */
  closeOnClick?: boolean;
};

export const DrawerOrPopoverMenuRadioItem = (props: DrawerOrPopoverMenuRadioItemProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? (
    <PopoverMenuRadioItem {...props} />
  ) : (
    <DrawerMenuRadioItem {...props} />
  );
};

export type DrawerOrPopoverMenuGroupProps = BlockProps;

export const DrawerOrPopoverMenuGroup = (props: DrawerOrPopoverMenuGroupProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? <PopoverMenuGroup {...props} /> : <DrawerMenuGroup {...props} />;
};

export type DrawerOrPopoverMenuHeadingProps = BlockProps;

export const DrawerOrPopoverMenuHeading = (props: DrawerOrPopoverMenuHeadingProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? <PopoverMenuHeading {...props} /> : <DrawerMenuHeading {...props} />;
};

export type DrawerOrPopoverMenuDividerProps = BlockProps;

export const DrawerOrPopoverMenuDivider = (props: DrawerOrPopoverMenuDividerProps) => {
  const { isAboveBreakpoint } = useDrawerOrPopoverMenu();

  return isAboveBreakpoint ? <PopoverMenuDivider {...props} /> : <DrawerMenuDivider {...props} />;
};
